import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { FileWithPath, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { DocumentDropzone } from 'src/Components/Shared/DocumentDropzone';
import { StorageFileDto } from 'src/Services/API/generated';

import { MemoryImageEdit } from './MemoryImageEdit';
import styles from './MemoryImagesListEdit.module.scss';
import { Props } from './MemoryImagesListEdit.props';

/**
 * When editing/creating a memory, this component allow to edit the list of photos.
 *  - Uploading photos
 *  - Reordering them
 *  - Deleting them
 */
const MemoryImagesListEdit = ({ photos, setPhotos, isEdit, hideTitle }: Props) => {
  const { t } = useTranslation();
  const maxCountOfPhotos = 10;

  const photosJSX = photos.map((photo, index) => {
    const onDelete = () => {
      setPhotos(photos => photos.filter(p => p.id != photo.id));
    };

    const onUploaded = (file: StorageFileDto) => {
      setPhotos(photos =>
        photos.map(p => {
          if (photo.id != p.id) return p;

          p.id = file.id;
          p.serverFile = file;
          return p;
        })
      );
    };

    return (
      <MemoryImageEdit
        key={photo.id}
        photo={photo}
        isCover={index === 0}
        onDelete={onDelete}
        onUploaded={onUploaded}
      />
    );
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    // We did finish a reordering drag
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPhotos(items => {
        const oldIndex = items.findIndex(photo => photo.id == active.id);
        const newIndex = items.findIndex(photo => photo.id == over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const onNewPhotos = (files: FileWithPath[]) => {
    setPhotos(photos => {
      // First compute the next id
      const nextId =
        photos.reduce((acc, photo) => {
          if (typeof photo.id === 'number' && photo.id > acc) return photo.id;
          return acc;
        }, -1) + 1;

      const filesToAdd = files.map((file, index) => ({ id: index + nextId, nativeFile: file }));
      photos.push(...filesToAdd);
      return photos.slice(0, maxCountOfPhotos);
    });
  };

  return (
    <div className={styles.container}>
      {!hideTitle && (
        <h1 className={styles.title}>
          {isEdit
            ? t('community.memory.edit', "Modifier l'album")
            : t('community.memory.createAlbum', 'Créer un album')}
        </h1>
      )}

      <p className={styles.explanation}>
        {t(
          'community.memory.createAlbumDescription',
          'Jusqu’à {{maxCountOfPhotos}} photos. Vous pourrez en ajouter d’autres plus tard.',
          { maxCountOfPhotos }
        )}
      </p>

      <DocumentDropzone
        title={
          photos.length === maxCountOfPhotos
            ? t('community.memory.photoCountLimitReached', 'Limite de photos atteinte')
            : t('community.memory.dragHere', 'Glissez les images ici ou cliquez pour sélectionner')
        }
        subtitle=""
        onDrop={onNewPhotos}
        accept={IMAGE_MIME_TYPE}
        // 20Mb max
        maxSize={20 * 1024 ** 2}
        maxFiles={maxCountOfPhotos - photos.length}
        disabled={photos.length === maxCountOfPhotos}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={photos} strategy={rectSortingStrategy}>
          <div className={styles.photosList}>{photosJSX}</div>
        </SortableContext>
      </DndContext>

      <div className={styles.tip}>
        <IconInfoCircle display="inline" />

        <span className={styles.tipText}>
          {t(
            'community.memory.reorderPhotosTip',
            'Glissez pour changer l’ordre. La première photo est la couverture.'
          )}
        </span>
      </div>
    </div>
  );
};

export default MemoryImagesListEdit;
